import {
  Insights,
  MonthBucket,
  MonthSegment,
  ORG_PALETTE,
  OrgStat,
} from "./useInsights";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MONTH_COUNTS = [3, 5, 4, 7, 6, 9, 5, 8, 6, 10, 7, 11];

// Sample organizations, pre-ranked, each given its palette color by rank so
// the blurred dashboard shows the same color story (leaderboard + stacks).
const ORGS: OrgStat[] = [
  { id: 1, name: "Metropolitan Symphony", count: 28, hours: 71 },
  { id: 2, name: "City Opera Orchestra", count: 19, hours: 52 },
  { id: 3, name: "Riverside Chamber Players", count: 14, hours: 33 },
  { id: 4, name: "Grand Theatre Pit", count: 11, hours: 27 },
  { id: 5, name: "Festival Ensemble", count: 6, hours: 12 },
  { id: 6, name: "Studio Sessions Co.", count: 3, hours: 8 },
].map((o, i) => ({ ...o, color: ORG_PALETTE[i] }));

/**
 * Distributes a month's services across three rotating organizations so the
 * placeholder stacked bars look varied and multi-colored.
 *
 * @param monthIndex 0-based month index
 * @param count services that month
 * @returns {MonthSegment[]} the month's organization slices
 */
function segmentsFor(monthIndex: number, count: number): MonthSegment[] {
  const picks = [0, 1, 2].map((k) => ORGS[(monthIndex + k) % ORGS.length]);
  const a = Math.round(count * 0.5);
  const b = Math.round(count * 0.3);
  const c = Math.max(0, count - a - b);
  const alloc = [a, b, c];
  return picks
    .map((org, k) => ({
      id: org.id,
      name: org.name,
      count: alloc[k],
      hours: alloc[k] * 2.5,
      color: org.color,
    }))
    .filter((s) => s.count > 0);
}

const months: MonthBucket[] = MONTH_COUNTS.map((count, i) => ({
  key: `2025-${`${i + 1}`.padStart(2, "0")}`,
  label: MONTH_LABELS[i],
  year: 2025,
  count,
  hours: count * 2.5,
  segments: segmentsFor(i, count),
}));

/**
 * Plausible-looking placeholder insights rendered (blurred) for non-Pro
 * members so the page reads as a rich, populated dashboard behind the lock.
 * No real account data is ever used here.
 */
export const SAMPLE_INSIGHTS: Insights = {
  isLoading: false,
  hasAnyData: true,
  servicesCount: 81,
  totalHours: 203,
  organizationsCount: 6,
  venuesCount: 11,
  avgPerMonth: 81 / 12,
  topOrg: ORGS[0],
  busiestMonth: months[11],
  organizations: ORGS,
  venues: [
    {
      id: 1,
      name: "Walt Disney Concert Hall",
      location: "Los Angeles, CA",
      count: 22,
      hours: 56,
    },
    {
      id: 2,
      name: "Carnegie Hall",
      location: "New York, NY",
      count: 14,
      hours: 38,
    },
    {
      id: 3,
      name: "Davies Symphony Hall",
      location: "San Francisco, CA",
      count: 11,
      hours: 27,
    },
    {
      id: 4,
      name: "The Soraya",
      location: "Northridge, CA",
      count: 9,
      hours: 20,
    },
    {
      id: 5,
      name: "Capitol Studios",
      location: "Los Angeles, CA",
      count: 6,
      hours: 18,
    },
    {
      id: 6,
      name: "Segerstrom Hall",
      location: "Costa Mesa, CA",
      count: 5,
      hours: 11,
    },
  ],
  months,
  types: [
    {
      sessionTypeID: 2,
      title: "Performance",
      icon: "fas fa-theater-masks",
      count: 38,
      hours: 95,
    },
    {
      sessionTypeID: 13,
      title: "Rehearsal",
      icon: "fas fa-list-music",
      count: 27,
      hours: 68,
    },
    {
      sessionTypeID: 1,
      title: "Recording Session",
      icon: "fas fa-microphone",
      count: 9,
      hours: 27,
    },
    {
      sessionTypeID: 14,
      title: "Dress Rehearsal",
      icon: "fas fa-user-tie",
      count: 7,
      hours: 13,
    },
  ],
  // Non-Pro users see this data only behind the blur (pointer events disabled),
  // so no real services back the placeholder widgets.
  services: [],
  allServices: [],
};
